import { Link } from 'react-router-dom';
import { AlertTriangle, Check } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { PageTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { PluginIcon } from '@reflex/ui/components/shared/PluginIcon';
import { Callout } from '@/components/shared/Callout';
import type { PluginManifest } from '@/lib/plugin-registry';
import { pluginServiceSecretsHref } from '@/lib/routes';

export function PluginHeroSection({ plugin }: { plugin: PluginManifest }) {
  const isEnabled = plugin.enabled !== false;
  const unfulfilledSecrets = isEnabled
    ? (plugin.server?.requiredSecrets?.filter((s) => !s.fulfilled) ?? [])
    : [];
  const hasUnfulfilledSecrets = unfulfilledSecrets.length > 0;
  const hasUnfulfilledServiceSecrets = unfulfilledSecrets.some((s) => s.tier === 'service');

  return (
    <>
      <div className="flex items-start gap-4">
        <PluginIcon icon={plugin.icon} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <PageTitle>{plugin.name}</PageTitle>
            <Badge variant="secondary" className="text-xs font-normal tabular-nums">
              v{plugin.version}
            </Badge>
            {!isEnabled ? (
              <Badge
                variant="secondary"
                className="gap-1 text-xs font-normal text-status-accent-orange"
              >
                <AlertTriangle className="h-3 w-3" /> Disabled
              </Badge>
            ) : hasUnfulfilledSecrets ? (
              <Badge
                variant="secondary"
                className="gap-1 text-xs font-normal text-status-accent-orange"
                data-testid="plugin-hero-needs-config"
              >
                <AlertTriangle className="h-3 w-3" /> Needs configuration
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1 text-xs font-normal text-status-success">
                <Check className="h-3 w-3" /> Active
              </Badge>
            )}
          </div>
          {plugin.description && <PageDescription>{plugin.description}</PageDescription>}
          {plugin.tags && plugin.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {plugin.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs font-normal">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {!isEnabled && plugin.disabledReason && (
        <div data-testid="plugin-hero-disabled-reason">
          <Callout>
            <PageDescription className="mt-0 text-sm">{plugin.disabledReason}</PageDescription>
          </Callout>
        </div>
      )}

      {/* Enabled but missing operator-configured secrets — surface the
          unfulfilled keys at the top of the page so the operator sees
          exactly what's blocking the plugin without scrolling to the
          Required Secrets section. Service-tier keys deep-link to
          Admin → Service Secrets so setup is not a dead end. */}
      {isEnabled && hasUnfulfilledSecrets && (
        <div data-testid="plugin-hero-unfulfilled-secrets">
          <Callout>
            <PageDescription className="mt-0 text-sm">
              Plugin is enabled but{' '}
              {unfulfilledSecrets.length === 1 ? 'a required secret is' : 'required secrets are'}{' '}
              not configured: {unfulfilledSecrets.map((s) => s.key).join(', ')}
              {hasUnfulfilledServiceSecrets && (
                <>
                  .{' '}
                  <Link
                    to={pluginServiceSecretsHref(plugin.name)}
                    className="underline hover:text-foreground"
                    data-testid="plugin-hero-service-secrets-link"
                  >
                    Configure in Service Secrets
                  </Link>
                </>
              )}
            </PageDescription>
          </Callout>
        </div>
      )}
    </>
  );
}
